import { useMemo, useCallback } from 'react';

import { Button } from '@/components/Button';
import { HTMLParagraph } from '@/components/HTMLParagraph';
import { StyledMainImage, StyledImageGrid, StyledImage } from '@/styles/images';
import { createSubtitle } from '@/utils/createSubtitleItineryProduct';
import { getIcon } from '@/utils/getIcon';

import {
  StyledContentGrid,
  StyledImageContainer,
  StyledRoomInfo,
  StyledTitleHeader,
  StyledFlexRow,
  StyledFacilitiesFlexbox,
} from './styles';

import type { ItineraryProduct } from '@/types/products';

interface ModalItemProps {
  roomProduct: ItineraryProduct;
}

export function ModalItem({ roomProduct }: ModalItemProps) {
  const headerImage = useMemo(
    () => (room: ItineraryProduct) => {
      const image = room.Content.filter((content) => content.type.toLowerCase() === 'main')[0].RichContent?.filter(
        (richContent) => richContent?.subType === 'header' && richContent?.type === 'image',
      )[0]?.path;

      if (image) {
        return image;
      }

      return room.Content.filter((content) => content.type.toLowerCase() === 'main')[0].RichContent?.filter(
        (richContent) => richContent?.type === 'image',
      )[0]?.path;
    },
    [],
  );

  const formatPrice = useCallback(
    (price: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(price),
    [],
  );

  return (
    <StyledContentGrid>
      <StyledImageContainer>
        <StyledMainImage modal src={headerImage(roomProduct)} alt="Hotel header image" />
        <StyledImageGrid>
          {roomProduct.Content.filter((content) => content.type.toLowerCase() === 'main')[0]
            .RichContent?.filter((richContent) => richContent?.type === 'image' && richContent?.subType !== 'header')
            .slice(0, 4)
            .map((richContent) => <StyledImage modal key={richContent.id} src={richContent.path} alt="Hotel image" />)}
        </StyledImageGrid>
      </StyledImageContainer>

      <StyledRoomInfo>
        <StyledTitleHeader>
          <span>{roomProduct.name}</span>
          <StyledFlexRow>
            <p>{roomProduct.type}</p>
            <span>&#8226;</span>
            <p>{createSubtitle(roomProduct)}</p>
          </StyledFlexRow>
        </StyledTitleHeader>
        <HTMLParagraph
          dangerousHTML={
            roomProduct.Content.filter((content) => content.type.toLowerCase() === 'main')[0].paragraph ?? ''
          }
        />
        {roomProduct.productFacilities && (
          <StyledFacilitiesFlexbox>
            {roomProduct.productFacilities.map((facility) => (
              <p key={facility}>
                {getIcon(facility)} &nbsp;
                {facility}
              </p>
            ))}
          </StyledFacilitiesFlexbox>
        )}
        <Button variant="secondary" size="small">
          Select + {formatPrice(roomProduct.sellAmount)}
        </Button>
      </StyledRoomInfo>
    </StyledContentGrid>
  );
}
